import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ClipboardCheck, 
  FileSearch, 
  Shield, 
  CheckCircle, 
  ArrowRight,
  Building,
  BarChart3,
  FileText,
  Users,
  Landmark,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, serviceSchema } from '../utils/seoSchemas';

import RelatedServices from '../components/RelatedServices';
const AuditServicesUAE = () => {
  usePageSeo({
    title: 'Audit Services in UAE | External & Internal Audit',
    description:
      'Professional audit services for UAE Free Zone & Mainland companies — external, internal, IFRS financials & license renewal audits by WPAA.',
    path: '/audit-services-uae'
  });

  const services = [
    {
      title: 'External Audit (Free Zone & Mainland)',
      description: 'Independent examination of financial statements for companies operating in UAE Free Zones and Mainland.',
      icon: FileSearch
    },
    {
      title: 'Internal Audit',
      description: 'Systematic evaluation of internal controls, risk management, and governance processes.',
      icon: Shield
    },
    {
      title: 'IFRS Financial Statement Preparation',
      description: 'Preparation of financial statements in compliance with International Financial Reporting Standards.',
      icon: FileText
    },
    {
      title: 'Audit Reports for License Renewal',
      description: 'Timely audit reports required for trade license renewal with Free Zone authorities.',
      icon: RefreshCw
    },
    {
      title: 'Compliance & Risk Assessment',
      description: 'Comprehensive assessment of regulatory compliance and identification of business risks.',
      icon: BarChart3
    }
  ];

  const whoNeedsAudit = [
    { text: 'Free Zone companies (DIC, DMCC, SAIF, Hamriyah, etc.)', icon: Building },
    { text: 'Companies requiring audited financial statements', icon: FileText },
    { text: 'Businesses preparing for license renewal', icon: RefreshCw },
    { text: 'Organizations needing internal control review', icon: Shield }
  ];

  const whyChooseUs = [
    'Experienced audit professionals',
    'Compliance with UAE regulations & IFRS',
    'Timely audit completion',
    'Professional and reliable reporting'
  ];

  return (
    <div className="min-h-screen">
      <JsonLd
        id="ld-audit-service"
        data={serviceSchema({
          name: 'Audit Services in UAE',
          description:
            'External and internal audit, IFRS financial statement preparation, license renewal audits and risk assessment for UAE Free Zone and Mainland companies.',
          path: '/audit-services-uae',
          serviceType: 'Audit Services'
        })}
      />
      <JsonLd
        id="ld-audit-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'Audit Services UAE', path: '/audit-services-uae' }
        ])}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-green-900 via-green-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-green-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-green-300 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <ClipboardCheck className="text-green-400" size={32} />
              <span className="text-green-400 font-semibold text-sm uppercase tracking-wider">External & Internal Audit</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Audit Services in UAE
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl">
              Audit is not just a compliance requirement — it builds trust, transparency, and credibility for your business.
            </p>
            <p className="text-lg text-gray-400 mb-8 max-w-3xl">
              WPAA provides professional audit services for Free Zone and Mainland companies across UAE.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?service=audit">
                <Button size="lg" className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg">
                  Request Audit Quote
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="tel:+971501219341" data-testid="audit-call-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Our Audit Services */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Our Audit Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive audit solutions for businesses of all sizes in UAE.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-green-500 transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="bg-green-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="text-green-600" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Needs Audit Services */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-wider">Target Clients</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Who Needs Audit Services?</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {whoNeedsAudit.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="text-green-600" size={24} />
                    </div>
                    <p className="text-gray-700 font-medium pt-2">{item.text}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose WPAA */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-green-600 font-semibold text-sm uppercase tracking-wider">Why WPAA</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-8">Why Choose WPAA?</h2>
              <div className="space-y-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
                    <p className="text-lg text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-8 text-white">
              <Users className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Experienced Audit Team</h3>
              <p className="text-green-100 mb-6">
                Our team of qualified auditors brings years of experience across various industries, ensuring thorough and insightful audit engagements.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-3xl font-bold">18+</p>
                  <p className="text-sm text-green-200">Years Experience</p>
                </div>
                <div>
                  <p className="text-3xl font-bold">200+</p>
                  <p className="text-sm text-green-200">Clients Served</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

            <RelatedServices
        services={[
          { title: 'Accounting Services in UAE', description: 'Audit-ready bookkeeping and IFRS-compliant financial statements every month.', path: '/accounting-services-uae', accent: 'indigo' },
          { title: 'Corporate Tax Services', description: '9% UAE Corporate Tax registration, return filing, and Free Zone compliance.', path: '/corporate-tax-uae', accent: 'teal' },
          { title: 'VAT Services in UAE', description: 'VAT registration, filing, reconciliation, and FTA audit support.', path: '/vat-services-uae', accent: 'blue' }
        ]}
      />

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Need Professional Audit Services?</h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Contact us today for a consultation and discover how our audit services can benefit your business.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact?service=audit">
              <Button size="lg" className="bg-white text-green-700 hover:bg-gray-100 px-8 py-6 text-lg">
                Request Audit Quote
              </Button>
            </Link>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="audit-whatsapp-btn">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AuditServicesUAE;
